<template>
  <div class="characters-container">
    <!-- Purple -->
    <div ref="purpleRef" class="char purple" :style="purpleStyle">
      <div class="eyes" :style="purpleEyesStyle">
        <EyeBall v-for="i in 2" :key="'p'+i" :size="18" :pupil-size="7" :max-distance="5"
          eye-color="white" pupil-color="#2D2D2D" :is-blinking="isPurpleBlinking"
          :force-look-x="purpleLookX" :force-look-y="purpleLookY" />
      </div>
    </div>
    <!-- Black -->
    <div ref="blackRef" class="char black" :style="blackStyle">
      <div class="eyes" :style="blackEyesStyle">
        <EyeBall v-for="i in 2" :key="'b'+i" :size="16" :pupil-size="6" :max-distance="4"
          eye-color="white" pupil-color="#2D2D2D" :is-blinking="isBlackBlinking"
          :force-look-x="blackLookX" :force-look-y="blackLookY" />
      </div>
    </div>
    <!-- Orange -->
    <div ref="orangeRef" class="char orange" :style="orangeStyle">
      <div class="eyes" :style="orangeEyesStyle">
        <Pupil v-for="i in 2" :key="'o'+i" :size="12" :max-distance="5" pupil-color="#2D2D2D"
          :force-look-x="hiding ? -5 : undefined" :force-look-y="hiding ? -4 : undefined" />
      </div>
    </div>
    <!-- Yellow -->
    <div ref="yellowRef" class="char yellow" :style="yellowStyle">
      <div class="eyes" :style="yellowEyesStyle">
        <Pupil v-for="i in 2" :key="'y'+i" :size="12" :max-distance="5" pupil-color="#2D2D2D"
          :force-look-x="hiding ? -5 : undefined" :force-look-y="hiding ? -4 : undefined" />
      </div>
      <div class="mouth" :style="yellowMouthStyle" />
    </div>
  </div>
</template>

<script>
import EyeBall from './EyeBall.vue'
import Pupil from './Pupil.vue'

export default {
  name: 'AnimatedCharacters',
  components: { EyeBall, Pupil },
  props: {
    /** Whether the user is currently typing in an input */
    isTyping: { type: Boolean, default: false },
    /** Whether a secret field has content */
    hasSecret: { type: Boolean, default: false },
    /** Whether the secret is currently visible (e.g. password shown) */
    secretVisible: { type: Boolean, default: false },
  },
  data() {
    return {
      mouseX: 0,
      mouseY: 0,
      isPurpleBlinking: false,
      isBlackBlinking: false,
      isLookingAtEachOther: false,
      isPurplePeeking: false,
      purplePos: { faceX: 0, faceY: 0, bodySkew: 0 },
      blackPos: { faceX: 0, faceY: 0, bodySkew: 0 },
      yellowPos: { faceX: 0, faceY: 0, bodySkew: 0 },
      orangePos: { faceX: 0, faceY: 0, bodySkew: 0 },
      purpleBlinkTimer: 0,
      blackBlinkTimer: 0,
      peekT: 0,
      rafId: 0,
    }
  },
  computed: {
    // Derived state
    hiding() {
      return this.hasSecret && this.secretVisible
    },
    leaning() {
      return this.isTyping || (this.hasSecret && !this.secretVisible)
    },

    // Purple styles
    purpleStyle() {
      return {
        height: this.leaning ? '440px' : '400px',
        transform: this.hiding ? 'skewX(0deg)'
          : this.leaning ? `skewX(${this.purplePos.bodySkew - 12}deg) translateX(40px)`
          : `skewX(${this.purplePos.bodySkew}deg)`,
      }
    },
    purpleEyesStyle() {
      return {
        left: this.hiding ? '20px' : this.isLookingAtEachOther ? '55px' : `${45 + this.purplePos.faceX}px`,
        top: this.hiding ? '35px' : this.isLookingAtEachOther ? '65px' : `${40 + this.purplePos.faceY}px`,
        gap: '32px',
      }
    },
    purpleLookX() {
      return this.hiding
        ? (this.isPurplePeeking ? 4 : -4)
        : this.isLookingAtEachOther ? 3 : undefined
    },
    purpleLookY() {
      return this.hiding
        ? (this.isPurplePeeking ? 5 : -4)
        : this.isLookingAtEachOther ? 4 : undefined
    },

    // Black styles
    blackStyle() {
      return {
        transform: this.hiding ? 'skewX(0deg)'
          : this.isLookingAtEachOther ? `skewX(${this.blackPos.bodySkew * 1.5 + 10}deg) translateX(20px)`
          : this.leaning ? `skewX(${this.blackPos.bodySkew * 1.5}deg)`
          : `skewX(${this.blackPos.bodySkew}deg)`,
      }
    },
    blackEyesStyle() {
      return {
        left: this.hiding ? '10px' : this.isLookingAtEachOther ? '32px' : `${26 + this.blackPos.faceX}px`,
        top: this.hiding ? '28px' : this.isLookingAtEachOther ? '12px' : `${32 + this.blackPos.faceY}px`,
        gap: '24px',
      }
    },
    blackLookX() {
      return this.hiding ? -4 : this.isLookingAtEachOther ? 0 : undefined
    },
    blackLookY() {
      return this.hiding ? -4 : this.isLookingAtEachOther ? -4 : undefined
    },

    // Orange styles
    orangeStyle() {
      return {
        transform: this.hiding ? 'skewX(0deg)' : `skewX(${this.orangePos.bodySkew}deg)`,
      }
    },
    orangeEyesStyle() {
      return {
        left: this.hiding ? '50px' : `${82 + this.orangePos.faceX}px`,
        top: this.hiding ? '85px' : `${90 + this.orangePos.faceY}px`,
        gap: '32px',
      }
    },

    // Yellow styles
    yellowStyle() {
      return {
        transform: this.hiding ? 'skewX(0deg)' : `skewX(${this.yellowPos.bodySkew}deg)`,
      }
    },
    yellowEyesStyle() {
      return {
        left: this.hiding ? '20px' : `${52 + this.yellowPos.faceX}px`,
        top: this.hiding ? '35px' : `${40 + this.yellowPos.faceY}px`,
        gap: '24px',
      }
    },
    yellowMouthStyle() {
      return {
        left: this.hiding ? '10px' : `${40 + this.yellowPos.faceX}px`,
        top: this.hiding ? '88px' : `${88 + this.yellowPos.faceY}px`,
      }
    },
  },
  watch: {
    isTyping(v) {
      if (v) {
        this.isLookingAtEachOther = true
        setTimeout(() => { this.isLookingAtEachOther = false }, 800)
      } else {
        this.isLookingAtEachOther = false
      }
    },
    hasSecret() {
      this.schedulePeek()
    },
    secretVisible() {
      this.schedulePeek()
    },
  },
  methods: {
    calcPos(el, target) {
      if (!el) return
      const r = el.getBoundingClientRect()
      const dx = this.mouseX - (r.left + r.width / 2)
      const dy = this.mouseY - (r.top + r.height / 3)
      target.faceX = Math.max(-15, Math.min(15, dx / 20))
      target.faceY = Math.max(-10, Math.min(10, dy / 30))
      target.bodySkew = Math.max(-6, Math.min(6, -dx / 120))
    },
    tick() {
      this.calcPos(this.$refs.purpleRef, this.purplePos)
      this.calcPos(this.$refs.blackRef, this.blackPos)
      this.calcPos(this.$refs.yellowRef, this.yellowPos)
      this.calcPos(this.$refs.orangeRef, this.orangePos)
      this.rafId = requestAnimationFrame(this.tick)
    },
    onMouseMove(e) {
      this.mouseX = e.clientX
      this.mouseY = e.clientY
    },
    startPurpleBlink() {
      this.purpleBlinkTimer = window.setTimeout(() => {
        this.isPurpleBlinking = true
        window.setTimeout(() => {
          this.isPurpleBlinking = false
          this.startPurpleBlink()
        }, 150)
      }, Math.random() * 4000 + 3000)
    },
    startBlackBlink() {
      this.blackBlinkTimer = window.setTimeout(() => {
        this.isBlackBlinking = true
        window.setTimeout(() => {
          this.isBlackBlinking = false
          this.startBlackBlink()
        }, 150)
      }, Math.random() * 4000 + 3000)
    },
    schedulePeek() {
      clearTimeout(this.peekT)
      if (this.hasSecret && this.secretVisible) {
        this.peekT = window.setTimeout(() => {
          this.isPurplePeeking = true
          this.peekT = window.setTimeout(() => {
            this.isPurplePeeking = false
            this.schedulePeek()
          }, 800)
        }, Math.random() * 3000 + 2000)
      } else {
        this.isPurplePeeking = false
      }
    },
  },
  mounted() {
    window.addEventListener('mousemove', this.onMouseMove)
    this.startPurpleBlink()
    this.startBlackBlink()
    this.schedulePeek()
    this.rafId = requestAnimationFrame(this.tick)
  },
  beforeDestroy() {
    window.removeEventListener('mousemove', this.onMouseMove)
    cancelAnimationFrame(this.rafId)
    clearTimeout(this.purpleBlinkTimer)
    clearTimeout(this.blackBlinkTimer)
    clearTimeout(this.peekT)
  },
}
</script>

<style scoped>
.characters-container { position: relative; width: 550px; height: 400px; }
.char { position: absolute; bottom: 0; transition: all 0.7s ease-in-out; transform-origin: bottom center; }
.purple { left: 70px; width: 180px; background: #6C3FF5; border-radius: 10px 10px 0 0; z-index: 1; }
.black { left: 240px; width: 120px; height: 310px; background: #2D2D2D; border-radius: 8px 8px 0 0; z-index: 2; }
.orange { left: 0; width: 240px; height: 200px; background: #FF9B6B; border-radius: 120px 120px 0 0; z-index: 3; }
.yellow { left: 310px; width: 140px; height: 230px; background: #E8D754; border-radius: 70px 70px 0 0; z-index: 4; }
.eyes { position: absolute; display: flex; transition: all 0.7s ease-in-out; }
.mouth { position: absolute; width: 80px; height: 4px; background: #2D2D2D; border-radius: 4px; transition: all 0.2s ease-out; }
</style>
